import { Box, Button, Divider, Slider, TextField, Typography } from '@mui/material';
import { Dispatch, FC, SetStateAction, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumberWithComma } from '../../utils/number';
import { interpolatePrice, interpolateView } from '../../utils/interpolate';
import axios from 'axios';
import { CampaignContext } from '../../contexts/CampaignContext.tsx';
import { formatBudget, getMaxValue, getMinValue } from '../../utils/biggestNumber.ts';
import Loading from '../common/Loading.tsx';

type IViewSlider = {
	view: number;
	getPrice: () => void;
	selectedBudget: number;
	setPrice?: (val: number) => void;
	viewMarks:
		| {
				view: number;
				price: number;
		  }[]
		| null;
	onChangeView: (newValue: number) => void;
	onBudgetChange: (newValue: number) => void;
	setViewMarks: Dispatch<
		SetStateAction<
			| {
					view: number;
					price: number;
			  }[]
			| null
		>
	>;
};

const ViewSlider: FC<IViewSlider> = ({ view, onChangeView, selectedBudget, onBudgetChange, getPrice, setPrice, viewMarks, setViewMarks }) => {
	const { t } = useTranslation();
	const { selectedPlatformId, selectedPlatform } = useContext(CampaignContext);
	useEffect(() => {
		getPrice();
		axios({
			method: 'GET',
			url: `/campaigns/view-prices`,
			params: {
				media_id: selectedPlatformId,
				type: 'view_campaign',
			},
		}).then(r => {
			if (r?.data?.success) {
				let data = r?.data?.data;
				Array.isArray(onChangeView(data[0].view));
				Array.isArray(onBudgetChange(data[0].price));
				setViewMarks(data);
				setPrice?.(getMinValue(data, 'price'));
			}
		});
	}, [selectedPlatform]);

	const handleChangeView = (_: Event, newValue: number | number[]) => {
		if (typeof newValue === 'number') {
			onChangeView(newValue);
			onBudgetChange(interpolatePrice(newValue, viewMarks ?? []) ?? 0);
		}
	};

	const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value.replace(/,/g, '');
		const newBudget = Number(value);
		onBudgetChange(newBudget);
		viewMarks && onChangeView(interpolateView(newBudget, viewMarks ?? []) ?? viewMarks[0].view);
	};
	const formatTooltip = (value: number) => {
		return `${value.toLocaleString()} هزار بازدید`;
	};

	let maxPrice = viewMarks && viewMarks.length > 0 ? getMaxValue(viewMarks, 'price') : 0;
	let minPrice = viewMarks && viewMarks.length > 0 ? getMinValue(viewMarks, 'price') : 0;
	let avgPrice = (minPrice + maxPrice) / 2;
	return (
		<Box className="relative">
			{!viewMarks ? (
				<Loading />
			) : (
				viewMarks &&
				!viewMarks.length && (
					<Box className="flex justify-center">
						<Typography className="absolute top-14 z-40 text-center text-lg">{t('the_admin_has_not_set_a_price_for_this_media')}</Typography>
					</Box>
				)
			)}
			<Box className={`${viewMarks && viewMarks.length === 0 && 'blur-sm'} mt-4 flex h-36 items-center justify-between rounded-lg bg-background-paper p-6`}>
				<Box className="flex items-center">
					<Typography className="me-8 text-xl text-text-primary">{t('view_count')}</Typography>
					{viewMarks && <Slider size="small" step={1000} min={viewMarks[0]?.view} max={viewMarks[viewMarks.length - 1]?.view} className="min-w-52" marks={viewMarks?.map(viewMark => ({ value: viewMark?.view, label: viewMark.view?.toLocaleString() }))} value={view} onChange={handleChangeView} valueLabelDisplay="on" valueLabelFormat={formatTooltip} />}
				</Box>
				<Divider orientation="vertical" className="mx-12 h-full rounded-lg border border-grey-200" />
				<Box className="flex w-full grow basis-0 items-start">
					<Typography className="me-2 min-w-28 text-xl text-text-primary">{t('budget')}</Typography>
					<Box className="flex w-full flex-col gap-y-4">
						<TextField className="text-left" size="small" fullWidth value={maxPrice <= selectedBudget ? formatNumberWithComma(maxPrice) : formatNumberWithComma(selectedBudget)} onChange={handleBudgetChange} inputProps={{ style: { direction: 'ltr' } }} />
						<Box className="flex items-center gap-x-3">
							{/* TODO */}
							{viewMarks &&
								viewMarks.length > 0 &&
								[minPrice, avgPrice, maxPrice].map(budget => (
									<Button key={budget} className={`rounded ${maxPrice < budget ? 'bg-action-disabledBackground text-action-disabled' : selectedBudget === budget ? 'bg-primary text-white' : 'bg-primary-focus text-primary-main'} shadow-none`} disabled={maxPrice >= budget ? false : true} onClick={() => handleBudgetChange({ target: { value: budget.toString() } } as React.ChangeEvent<HTMLInputElement>)}>
										{formatBudget(budget)}
									</Button>
								))}
						</Box>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default ViewSlider;
